// Tiny Handlebars-subset template engine for the `markdown` render
// primitive. Renders a template string against a render step's data
// (typically the list of row objects from an analytics_query / python
// step) into a markdown string, which the markdown primitive then
// hands to <Markdown/> for display.
//
// Supports exactly three constructs — "basic looping, branching, and
// variable interpolation":
//
//   {{ path }}                      interpolation (dotted paths, `this`, @index)
//   {{ path | format }}             interpolation through kernelFormat.formatValue
//   {{#each path}} … {{/each}}      iterate an array (with optional {{else}})
//   {{#if path}} … {{else}} … {{/if}}   truthy branch
//
// Deliberately NOT a general template language: no partials, no custom
// helpers, no inline expressions. Determinism and a tiny surface are
// the point — the kernel runner has no LLM, and the template is authored
// once by the kernel-save agent, so the engine must be predictable and
// safe to re-run against fresh data forever.
//
// Output is markdown text; the renderer turns it into React nodes (no
// dangerouslySetInnerHTML anywhere in Markdown.jsx), so interpolated
// data can't inject markup — at worst it influences markdown structure,
// which is the whole point of a markdown renderer.

import { formatValue } from "./kernelFormat.js";

const TAG_RE = /\{\{([\s\S]*?)\}\}/g;

// Build the root scope from whatever the renderSource step produced.
// An array → exposed as `rows` (so `{{#each rows}}` is the canonical
// loop). A single object → its keys are merged at the top level so
// `{{ field }}` works directly. A scalar → exposed as `value`. `data`
// always points at the raw output for callers that want it explicit.
function rootScope(data) {
    if (Array.isArray(data)) {
        return { rows: data, data };
    }
    if (data && typeof data === "object") {
        // Expose the object's own keys at the top level; keep `rows`
        // loop-able if the object itself carries a rows array, else
        // default to empty (don't clobber a real `rows` field).
        const rows = Array.isArray(data.rows) ? data.rows : [];
        return { ...data, rows, data };
    }
    return { rows: [], data, value: data };
}

export function renderTemplate(template, data) {
    if (typeof template !== "string" || !template) return "";
    const ast = parse(template);
    const root = { value: rootScope(data), meta: {} };
    return evalNodes(ast.body, [root]);
}

// ── parse ─────────────────────────────────────────────────────────

function parse(template) {
    const doc = { type: "doc", body: [] };
    // Each stack frame is the currently-open block plus which arm
    // (`body` before {{else}}, `alt` after) new nodes append to.
    const stack = [{ node: doc, arm: "body" }];
    const pushText = (text) => {
        if (!text) return;
        const top = stack[stack.length - 1];
        top.node[top.arm].push({ type: "text", value: text });
    };
    const pushNode = (n) => {
        const top = stack[stack.length - 1];
        top.node[top.arm].push(n);
    };

    // Collect every {{...}} tag with its position so we can apply the
    // "standalone tag" whitespace rule below.
    const tags = [];
    let m;
    TAG_RE.lastIndex = 0;
    while ((m = TAG_RE.exec(template))) {
        tags.push({ inner: m[1].trim(), start: m.index, end: TAG_RE.lastIndex });
    }

    let cursor = 0;
    for (const tag of tags) {
        const raw = tag.inner;
        let textEnd = tag.start;
        let nextCursor = tag.end;
        // Mustache "standalone" rule: when a BLOCK tag (#each / #if /
        // else / a closer) is the only non-whitespace on its line, drop
        // that line's surrounding whitespace and its trailing newline.
        // Without this, the natural readable layout — {{#each}} and
        // {{/each}} on their own lines — injects a blank line per
        // iteration, which silently breaks GFM tables and tight lists
        // in the rendered markdown (a blank line ends both). Plain
        // {{interpolation}} is never standalone; it renders in place.
        if (isBlockTag(raw)) {
            const sa = standaloneSpan(template, tag.start, tag.end);
            if (sa) { textEnd = sa.textEnd; nextCursor = sa.nextCursor; }
        }
        pushText(template.slice(cursor, textEnd));
        cursor = nextCursor;

        if (!raw) continue;
        if (raw.startsWith("#each ")) {
            const node = { type: "each", path: raw.slice(6).trim(), body: [], alt: [] };
            pushNode(node);
            stack.push({ node, arm: "body" });
        } else if (raw.startsWith("#if ")) {
            const node = { type: "if", path: raw.slice(4).trim(), body: [], alt: [] };
            pushNode(node);
            stack.push({ node, arm: "body" });
        } else if (raw === "else") {
            // Flip the innermost open block to its else arm. A stray
            // {{else}} at the top level is ignored (no open block).
            if (stack.length > 1) stack[stack.length - 1].arm = "alt";
        } else if (raw.startsWith("/")) {
            // Lenient close: any {{/x}} closes the innermost block. A
            // mismatched tag in an LLM-authored template degrades to a
            // close rather than throwing.
            if (stack.length > 1) stack.pop();
        } else {
            const [path, fmt] = raw.split("|").map((s) => s.trim());
            pushNode({ type: "var", path, fmt: fmt || null });
        }
    }
    pushText(template.slice(cursor));
    return doc;
}

// A block tag opens/closes a section ({{#each}}, {{#if}}, {{/…}}) or is
// {{else}} — these are the standalone-eligible tags. Interpolations are
// not (they always render in place, wherever they sit on a line).
function isBlockTag(inner) {
    return inner === "else" || inner.startsWith("#") || inner.startsWith("/");
}

function isInlineWhitespace(c) {
    return c === " " || c === "\t" || c === "\r";
}

// If the tag spanning [start, end) is the only non-whitespace on its
// line, return the adjusted span: `textEnd` keeps everything up to (and
// including) the preceding newline but drops the line's leading
// indentation, and `nextCursor` skips the trailing whitespace plus the
// newline after the tag. Returns null when the tag shares its line with
// other content (then it must render in place). Mirrors the Mustache
// standalone-line algorithm; start/end of input count as line bounds.
function standaloneSpan(s, start, end) {
    let i = start;
    while (i > 0 && isInlineWhitespace(s[i - 1])) i--;
    const leftOk = i === 0 || s[i - 1] === "\n";
    if (!leftOk) return null;

    let j = end;
    while (j < s.length && isInlineWhitespace(s[j])) j++;
    const rightOk = j === s.length || s[j] === "\n";
    if (!rightOk) return null;

    const nextCursor = j < s.length && s[j] === "\n" ? j + 1 : j;
    return { textEnd: i, nextCursor };
}

// ── evaluate ──────────────────────────────────────────────────────

function evalNodes(nodes, frames) {
    let out = "";
    for (const n of nodes) out += evalNode(n, frames);
    return out;
}

function evalNode(n, frames) {
    if (n.type === "text") return n.value;
    if (n.type === "var") {
        const v = lookup(frames, n.path);
        return formatValue(v, n.fmt || "text");
    }
    if (n.type === "if") {
        const v = lookup(frames, n.path);
        return evalNodes(truthy(v) ? n.body : n.alt, frames);
    }
    if (n.type === "each") {
        const v = lookup(frames, n.path);
        const arr = Array.isArray(v)
            ? v
            : v && typeof v === "object"
                ? Object.values(v)
                : [];
        if (!arr.length) return evalNodes(n.alt, frames);
        let out = "";
        for (let i = 0; i < arr.length; i++) {
            const frame = {
                value: arr[i],
                meta: { index: i, first: i === 0, last: i === arr.length - 1, length: arr.length },
            };
            out += evalNodes(n.body, [frame, ...frames]);
        }
        return out;
    }
    return "";
}

// Resolve a path against the scope chain (innermost frame first). `this`
// is the current frame's value; `@index` / `@first` / `@last` / `@length`
// read loop metadata. A dotted name resolves in the nearest frame whose
// value actually carries it, so a loop body can still reach outer fields.
function lookup(frames, path) {
    if (!path) return undefined;
    if (path === "this" || path === ".") return frames[0]?.value;
    if (path.startsWith("@")) {
        const key = path.slice(1);
        for (const f of frames) {
            if (f.meta && key in f.meta) return f.meta[key];
        }
        return undefined;
    }
    const parts = path.split(".");
    for (const f of frames) {
        const r = resolvePath(f.value, parts);
        if (r !== undefined) return r;
    }
    return undefined;
}

function resolvePath(obj, parts) {
    let cur = obj;
    for (const p of parts) {
        if (cur == null || typeof cur !== "object") return undefined;
        cur = cur[p];
    }
    return cur;
}

// Empty arrays and the usual falsy scalars (0, "", null, undefined,
// false, NaN) are falsy; everything else is truthy. Matches the
// intuition behind `{{#if rows}}` (no rows → show the empty branch).
function truthy(v) {
    if (Array.isArray(v)) return v.length > 0;
    return Boolean(v);
}
